import React, { Fragment, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerContext from "../../Context/CustomerContext";
import LogicContext from "../../Context/LogicContext";
import { customerService } from "../../services/customer.service";
import { debounce } from "lodash";
import { Combobox, Transition } from "@headlessui/react";
import { SetCustomerSearchList } from "../../store/customerAtom";

function CustomerSearch() {
  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [textInput, setTextInput] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);
  const setCustomerSearchList = SetCustomerSearchList();

  const [moduleId, moduleName, userMenu] = pathname.split("/").slice(1, 4);

  const debouncedSearch = React.useRef(
    debounce((search: string) => {
      if (search.length > 2) {
        customerService.getCustomerBySearch(search).then((res) => {
          if (res?.data?.length) {
            setSearchResult(res.data);
            setCustomerSearchList(res.data);
          } else {
            logicContext.showToast("No available record for your search query!", "info");
            setSearchResult([]);
          }
        }).catch(() => {
          setSearchResult([]);
        });
      } else {
        setSearchResult([]);
      }
    }, 500)
  ).current;

  const handleSelect = (customer: any) => {
    if (customer) {
      navigate(`/${moduleId}/${moduleName}/${userMenu}/customer-details/${customer.Customer_Id}`);
      setTextInput(""); // Optional: clear input after selection
    }
  };

  return (
    <Combobox onChange={handleSelect}>
      {({ open }) => (
        <div className="relative max-w-sm w-full">
          {/* Search Input */}
          <Combobox.Input
            className="w-full rounded-full border font-medium border-gray-300 bg-white py-1.5 pl-10 pr-4 text-sm text-gray-800 placeholder-gray-400 focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
            placeholder="Search customer..."
            displayValue={(customer: any) => customer?.Name || textInput}
            onChange={(e) => {
              const val = e.target.value;
              setTextInput(val);
              debouncedSearch(val);
            }}
          />

          {/* Search Icon */}
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5 text-gray-500"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
          </div>

          {/* Custom Dropdown Results */}
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setTextInput("")}
          >
            <Combobox.Options
              static
              className="absolute z-50 mt-2 max-h-96 w-full overflow-auto rounded-md bg-white py-3 text-sm shadow-xl ring-1 ring-black/10 focus:outline-none"
            >
              {searchResult.length === 0 && textInput.length > 2 ? (
                <div className="px-6 py-3 text-center text-gray-500">
                  No customers found.
                </div>
              ) : (
                searchResult.map((customer: any, index: number) => (
                  <Combobox.Option
                    key={customer.Customer_Id || index}
                    value={customer}
                    className="cursor-pointer hover:bg-gray-50 transition"
                  >
                    {({ active }) => (
                      <div
                        className={`px-2 py-2 border-b border-gray-200 last:border-b-0 ${
                          active ? "bg-blue-50" : ""
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          <span className="font-normal">{customer.Name || "-"}</span>
                        </div>
                        <div className="text-gray-600">
                         <span className="font-normal">{customer.Email_Address || "-"}</span>
                        </div>
                        <div className="text-gray-600">
                           <span className="font-normal">{customer.Mobile || customer.Phone || "-"}</span>
                        </div>

                        {index !== searchResult.length - 1 && (
                          <div className="mt-2 border-t border-gray-300"></div>
                        )}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

export default CustomerSearch;